import { apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import { entityListKey } from '../../../shared/api/entityQueryKeys.js';

export type CandidateType = 'note' | 'task' | 'contact_interaction' | 'contact';
export type CandidateStatus =
  | 'pending'
  | 'needs_context'
  | 'stale'
  | 'rejected'
  | 'expired'
  | 'succeeded';
export type CandidatePayload =
  | { type: 'note'; body: string }
  | { type: 'task'; title: string; description: string | null; dueAt: string | null }
  | {
      type: 'contact_interaction';
      contactId: string;
      interactionType: 'call' | 'email' | 'meeting' | 'note' | 'sms' | 'in_app';
      subject: string | null;
      body: string | null;
      channel: string | null;
      occurredAt: string | null;
    }
  | {
      /** C2 lead conversion: explicit fields only; `contactId` set means the
       * reviewer linked an existing contact instead of creating a duplicate. */
      type: 'contact';
      contactId: string | null;
      firstName: string;
      lastName: string | null;
      phone: string | null;
      email: string | null;
      createDeal: boolean;
      dealTitle: string | null;
      dealAmount: number | null;
    };

export interface TelegramCandidateItem {
  id: string;
  type: CandidateType;
  typeLabel: string;
  status: CandidateStatus;
  statusLabel: string;
  confidence: number;
  stateVersion: number;
  sourceExcerpt: string | null;
  proposal: Record<string, unknown> | null;
  finalPayload: CandidatePayload | null;
  result:
    | { type: 'note'; id: string }
    | { type: 'task'; id: string }
    | { type: 'contact_interaction'; id: string; contactId: string }
    | { type: 'contact'; contactId: string; dealId: string | null }
    | null;
  outcomeCode: string | null;
  createdAt: string;
  expiresAt: string;
  reviewedAt: string | null;
}

export function telegramCandidatesKey(workspaceId: string, locale: string) {
  return entityListKey('telegram-candidates', workspaceId, { locale });
}

export async function listTelegramCandidates(
  locale: 'ru' | 'en',
): Promise<{ items: TelegramCandidateItem[] }> {
  return apiGet(`/integrations/telegram/group-candidates?locale=${locale}`);
}

export async function confirmTelegramCandidate(
  id: string,
  locale: string,
  expectedStateVersion: number,
  payload: CandidatePayload,
) {
  return apiPatch<
    TelegramCandidateItem,
    { expectedStateVersion: number; payload: CandidatePayload }
  >(`/integrations/telegram/group-candidates/${id}?locale=${locale}`, {
    expectedStateVersion,
    payload,
  });
}

export async function approveTelegramCandidate(
  id: string,
  locale: string,
  expectedStateVersion: number,
) {
  return apiPost<TelegramCandidateItem, { expectedStateVersion: number }>(
    `/integrations/telegram/group-candidates/${id}/approve?locale=${locale}`,
    { expectedStateVersion },
  );
}

export async function resolveTelegramCandidate(
  id: string,
  locale: string,
  expectedStateVersion: number,
  decision: 'reject' | 'needs_context',
) {
  return apiPost<
    TelegramCandidateItem,
    { expectedStateVersion: number; decision: 'reject' | 'needs_context' }
  >(`/integrations/telegram/group-candidates/${id}/resolve?locale=${locale}`, {
    expectedStateVersion,
    decision,
  });
}
